import { z } from 'zod';
import {
  ADDRESS_TEXT_REGEX,
  NAME_REGEX,
  displayToIsoDate,
  isoDateMinusYears,
  parseMoneyToNumber,
  POSTAL_CODE_REGEX,
  todayIsoDate,
} from '../utils';

/**
 * Field-level builders zod reutilizables a través del flujo de onboarding y
 * cualquier otra pantalla que valide los mismos formatos. El caller pasa los
 * mensajes ya traducidos (vía `t()` u otro mecanismo) para no acoplar este
 * paquete a la capa de i18n.
 *
 * Diseño:
 * - Cada builder devuelve un `z.ZodTypeAny` que se compone dentro de un
 *   `z.object({...})` en el componente.
 * - Los campos *opcionales* dejan pasar string vacío (skip de validación)
 *   para que la required-ness viva en el gate upstream — el formulario de
 *   data, por ejemplo, mantiene Nombre opcional a nivel schema pero el
 *   botón Continuar exige no-vacío.
 */

/**
 * Campo de nombre opcional. Acepta letras (incluye diacríticos), espacios,
 * guiones y apóstrofes. Rechaza dígitos y caracteres especiales.
 *
 * @example nameField(t('errors.nameInvalidChars'))
 */
export function nameField(invalidMessage: string) {
  return z
    .string()
    .refine(val => !val || NAME_REGEX.test(val), { message: invalidMessage })
    .optional();
}

/**
 * Campo de nombre requerido. Combina `min(1)` con el regex de nombre. Espejo de
 * `requiredAddressTextField`: los call-sites que necesitan que el campo sea
 * obligatorio (p.ej. captura manual de datos cuando el OCR falla) lo usan en
 * lugar de `nameField` + gate upstream.
 *
 * @example requiredNameField({
 *   requiredMessage: t('errors.paternalSurnameRequired'),
 *   invalidMessage: t('errors.nameInvalidChars'),
 * })
 */
export function requiredNameField(messages: { requiredMessage: string; invalidMessage: string }) {
  return z
    .string()
    .min(1, messages.requiredMessage)
    .refine(val => NAME_REGEX.test(val), { message: messages.invalidMessage });
}

// Límite conservador para campos de texto de dirección (G7: prevenir DoS
// con strings infinitos al BE / JSONB column). 200 caracteres cubre incluso
// las direcciones rurales más largas; el BE puede aplicar su propia
// validación pero el FE rechaza temprano.
const ADDRESS_FIELD_MAX_LENGTH = 200;

/**
 * Campo de texto de dirección opcional (ciudad, colonia, etc.). Acepta
 * letras, dígitos, espacios, punto, guion y apóstrofe. Rechaza otros
 * caracteres especiales.
 *
 * @example addressTextField(t('errors.addressInvalidChars'))
 */
export function addressTextField(invalidMessage: string) {
  return z
    .string()
    .max(ADDRESS_FIELD_MAX_LENGTH, invalidMessage)
    .refine(val => !val || ADDRESS_TEXT_REGEX.test(val), { message: invalidMessage })
    .optional();
}

/**
 * Campo de texto de dirección requerido. Combina min(1) con el regex.
 *
 * @example requiredAddressTextField({
 *   requiredMessage: t('errors.streetRequired'),
 *   invalidMessage: t('errors.addressInvalidChars'),
 * })
 */
export function requiredAddressTextField(messages: {
  requiredMessage: string;
  invalidMessage: string;
}) {
  return z
    .string()
    .min(1, messages.requiredMessage)
    .max(ADDRESS_FIELD_MAX_LENGTH, messages.invalidMessage)
    .refine(val => ADDRESS_TEXT_REGEX.test(val), { message: messages.invalidMessage });
}

/**
 * Campo de código postal mexicano (exactamente 5 dígitos). Requerido por
 * default; los call-sites que necesiten opcional pueden encadenar `.optional()`
 * sobre el retorno.
 *
 * El mismo mensaje se usa para "vacío" y "formato inválido" — en la práctica
 * el usuario solo necesita saber que debe ser 5 dígitos.
 *
 * @example postalCodeField(t('errors.postalCodeInvalid'))
 */
export function postalCodeField(invalidMessage: string) {
  return z.string().min(1, invalidMessage).regex(POSTAL_CODE_REGEX, invalidMessage);
}

/**
 * Campo de monto (string crudo del `MoneyInput`). Valida que no esté vacío y
 * que parsee a un número MAYOR a 0 — cierra el hueco de los schemas que solo
 * usaban `z.string().min(1)` (longitud, no positividad). Usa `parseMoneyToNumber`
 * (tolera `$`/comas/espacios/`MXN`). NO transforma el valor: sigue siendo string
 * para react-hook-form; la conversión a número/cents la hace el caller.
 *
 * @example moneyAmountField({ required: t('form.amountRequired'), invalid: t('form.amountInvalid') })
 */
export function moneyAmountField(messages: {
  required: string;
  invalid: string;
  /** Techo inclusivo en la MISMA unidad que se captura (pesos, no centavos). */
  max?: number;
  tooLarge?: string;
}) {
  const base = z
    .string()
    .min(1, messages.required)
    .refine(value => parseMoneyToNumber(value) > 0, { message: messages.invalid });

  if (messages.max == null) return base;

  const max = messages.max;
  return base.refine(value => parseMoneyToNumber(value) <= max, {
    message: messages.tooLarge ?? messages.invalid,
  });
}

/**
 * Campo de fecha en el pasado (formato display `DD/MM/YYYY`). Valida que sea
 * una fecha real del calendario —`displayToIsoDate` rechaza `30/02/2024`— y que
 * no sea futura. Es el `birthDateField` sin los límites de edad: sirve para
 * fechas de hechos ya ocurridos (constitución de una empresa, otorgamiento de
 * un poder), donde "hace 80 años" es perfectamente válido.
 *
 * @example pastDateField({
 *   requiredMessage: t('errors.constitutionDateRequired'),
 *   invalidMessage: t('errors.constitutionDateInvalid'),
 *   futureMessage: t('errors.constitutionDateFuture'),
 * })
 */
export function pastDateField(messages: {
  invalidMessage: string;
  futureMessage: string;
  requiredMessage?: string;
}) {
  const todayIso = todayIsoDate();

  const base = messages.requiredMessage ? z.string().min(1, messages.requiredMessage) : z.string();

  const refined = base
    .refine(val => !val || displayToIsoDate(val) !== null, { message: messages.invalidMessage })
    .refine(
      val => {
        if (!val) return true;
        const iso = displayToIsoDate(val);
        return iso === null || iso <= todayIso;
      },
      { message: messages.futureMessage }
    );

  return messages.requiredMessage ? refined : refined.optional();
}

/**
 * Campo de fecha de nacimiento, formato `DD/MM/AAAA` (el que produce
 * `DateInput` y espera `parseBirthDate`). Si trae contenido, debe ser una fecha
 * calendario real (rechaza `31/02/2020`, `77/77/1998`) y no puede ser futura.
 *
 * Por defecto es **opcional** (vacío pasa — la required-ness vive en el gate
 * upstream, mismo criterio que el resto de los campos de este archivo). Pasa
 * `requiredMessage` para volverlo **obligatorio** (vacío falla con ese mensaje).
 *
 * Rango de edad (opcional): `minAge`/`maxAge` acotan la edad plausible para
 * rechazar tanto menores de edad como fechas absurdas (`20/06/1500`). Requieren
 * su mensaje correspondiente para reportar el error.
 *
 * @example birthDateField({
 *   requiredMessage: t('errors.birthDateRequired'), // opcional
 *   invalidMessage: t('errors.birthDateFormatInvalid'),
 *   futureMessage: t('errors.birthDateFuture'),
 *   minAge: 18, minAgeMessage: t('errors.birthDateMinAge'),
 *   maxAge: 120, maxAgeMessage: t('errors.birthDateMaxAge'),
 * })
 */
export function birthDateField(messages: {
  invalidMessage: string;
  futureMessage: string;
  requiredMessage?: string;
  minAge?: number;
  maxAge?: number;
  minAgeMessage?: string;
  maxAgeMessage?: string;
}) {
  const todayIso = todayIsoDate();
  // Fecha máxima de nacimiento para tener ≥ minAge (nació hace suficiente).
  const latestBirthIso = messages.minAge != null ? isoDateMinusYears(messages.minAge) : null;
  // Fecha mínima de nacimiento para tener ≤ maxAge (no es absurdamente vieja).
  const earliestBirthIso = messages.maxAge != null ? isoDateMinusYears(messages.maxAge) : null;

  const base = messages.requiredMessage ? z.string().min(1, messages.requiredMessage) : z.string();

  const refined = base
    .refine(val => !val || displayToIsoDate(val) !== null, { message: messages.invalidMessage })
    .refine(
      val => {
        if (!val) return true;
        const iso = displayToIsoDate(val);
        return iso === null || iso <= todayIso;
      },
      { message: messages.futureMessage }
    )
    .refine(
      val => {
        if (!val || latestBirthIso === null) return true;
        const iso = displayToIsoDate(val);
        return iso === null || iso <= latestBirthIso;
      },
      { message: messages.minAgeMessage ?? messages.invalidMessage }
    )
    .refine(
      val => {
        if (!val || earliestBirthIso === null) return true;
        const iso = displayToIsoDate(val);
        return iso === null || iso >= earliestBirthIso;
      },
      { message: messages.maxAgeMessage ?? messages.invalidMessage }
    );

  return messages.requiredMessage ? refined : refined.optional();
}
